// ProductDetailsPage.js - Complete file with AboutVideo integration

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./ProductDetails.css";
import BeforeAfterSlider from "../Bestsellers/BeforeAfterSlider";
import ProductBenefits from "../Bestsellers/ProductBenefits";
import HomeBestsellersSection from "../Home/HomeBestsellersSection";
import HomeTrendingSection from "../Home/HomeTrendingSection";
import { getCompleteProduct, getProductSubsections, addToCartAPI } from "../api";
import { useCart } from "../../Context/CartContext";
import { useWishlist } from "../../Context/WishlistContext";
import { useCurrency } from "../../Context/CurrencyContext";
import AboutVideo from "../About/AboutVideo";
import ProductShowcase from "../Bestsellers/ProductShowcase";
import TestimonialSlider from "../Bestsellers/TestimonialSlider";
import FaqSection from "../Bestsellers/FaqSection";
import AboutSection from "../Bestsellers/AboutSection";
import HomeFeatures from "../Home/HomeFeatures"

const ProductDetailsPage = () => {
  const { id } = useParams(); 
  const navigate = useNavigate();
  const { addToCart, openCart } = useCart();
  const { addToWishlist, isInWishlist } = useWishlist();
  const { formatPrice, selectedCountry } = useCurrency();
  const wrapperRef = useRef(null);
  const infoRef = useRef(null);
  const [selectedMedia, setSelectedMedia] = useState(0);
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedFragrance, setSelectedFragrance] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [expandedSection, setExpandedSection] = useState(null);
  const [showFragranceDrawer, setShowFragranceDrawer] = useState(false);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [productVideos, setProductVideos] = useState([]);
  const [pairedProducts, setPairedProducts] = useState([]);
  const [productBenefits, setProductBenefits] = useState([]);
  const [beforeAfterData, setBeforeAfterData] = useState([]);
  const [benefitsData, setBenefitsData] = useState({ left: [], right: [], centerImage: null });
  const [videoErrors, setVideoErrors] = useState({});
  const [videoPlayingStates, setVideoPlayingStates] = useState({});
  const [subsectionsLoading, setSubsectionsLoading] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const subsectionsFetchedRef = useRef(false);
  
  // Video modal state
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [selectedProductForModal, setSelectedProductForModal] = useState(null);

  const API_BASE_URL = "https://volumaire.backendapihub.com";
  const DEFAULT_IMAGE = "/images/dailyroutine2.webp";

  // Function to handle body scroll lock when drawer is open
  useEffect(() => {
    if (showFragranceDrawer) {
      document.body.classList.add('drawer-open');
    } else {
      document.body.classList.remove('drawer-open');
    }
    
    return () => {
      document.body.classList.remove('drawer-open');
    };
  }, [showFragranceDrawer]);

  // BENEFITS ICON MAPPING (SVG icons) - FALLBACK ONLY
  const BENEFIT_ICONS = {
    vegan: "/icons/vegan.svg",
    organic: "/icons/organic.svg",
    recyclable: "/icons/recyclable.svg",
    cruelty: "/icons/cruelty-free.svg",
  };

  const getBenefitIcon = useCallback((tag) => {
    if (!tag) return "/icons/check.svg";
    const tagString = String(tag).toLowerCase();
    if (tagString.includes("vegan")) return BENEFIT_ICONS.vegan;
    if (tagString.includes("organic")) return BENEFIT_ICONS.organic;
    if (tagString.includes("recycl")) return BENEFIT_ICONS.recyclable;
    if (tagString.includes("cruelty")) return BENEFIT_ICONS.cruelty;
    return "/icons/check.svg";
  }, []);

  const isValidImageUrl = useCallback((url) => {
    if (!url) return false;
    const strUrl = String(url).trim();
    return strUrl !== "" && 
           strUrl !== "null" && 
           strUrl !== "undefined" && 
           !strUrl.includes('undefined') &&
           !strUrl.includes('null');
  }, []);

  const getImageSource = useCallback((apiImage) => {
    try {
      if (isValidImageUrl(apiImage)) {
        return apiImage;
      }
      return DEFAULT_IMAGE;
    } catch (error) {
      console.error("Error in getImageSource:", error);
      return DEFAULT_IMAGE;
    }
  }, [isValidImageUrl]);

  const constructFullUrl = useCallback((url) => {
    if (!url) return DEFAULT_IMAGE;
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    if (url.startsWith('/')) {
      return `${API_BASE_URL}${url}`;
    }
    return `${API_BASE_URL}/${url}`;
  }, []);

  const constructVideoUrl = useCallback((url) => {
    if (!url) return null;
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    if (url.startsWith('/')) {
      return `${API_BASE_URL}${url}`;
    }
    return `${API_BASE_URL}/${url}`;
  }, []);

  const handleImageError = useCallback((e) => {
    e.target.onerror = null;
    e.target.src = DEFAULT_IMAGE;
  }, []);

  const handleVideoError = useCallback((index, e) => {
    console.error(`Video ${index} failed to load:`, e.target.error);
    setVideoErrors(prev => ({
      ...prev,
      [index]: true
    }));
    const videoElement = e.target;
    const wrapper = videoElement.parentElement;
    if (wrapper) {
      wrapper.classList.add('video-error');
      videoElement.style.display = 'none';
    }
  }, []);

  const toggleVideoPlay = useCallback((videoId, e) => {
    if (e) e.stopPropagation();
    const videoElement = document.getElementById(videoId);
    if (videoElement) {
      if (videoElement.paused) {
        videoElement.play().catch(() => {});
        setVideoPlayingStates(prev => ({ ...prev, [videoId]: true }));
      } else {
        videoElement.pause();
        setVideoPlayingStates(prev => ({ ...prev, [videoId]: false }));
      }
    }
  }, []);

  const openVideoModal = useCallback((video, productInfo, e) => {
    if (e) e.stopPropagation();
    setSelectedVideo(video);
    setSelectedProductForModal(productInfo);
    setIsVideoModalOpen(true);
    document.body.style.overflow = 'hidden';
  }, []);

  const closeVideoModal = useCallback(() => {
    setIsVideoModalOpen(false);
    setSelectedVideo(null);
    setSelectedProductForModal(null);
    document.body.style.overflow = 'unset';
  }, []);

  const handleVideoPlay = useCallback((videoId) => {
    setVideoPlayingStates(prev => ({ ...prev, [videoId]: true }));
  }, []);

  const handleVideoPause = useCallback((videoId) => {
    setVideoPlayingStates(prev => ({ ...prev, [videoId]: false }));
  }, []);

  // Format benefits data for ProductBenefits component
  const formatBenefitsForDisplay = useCallback((benefits, centerImage) => {
    if (!benefits || benefits.length === 0) return { left: [], right: [], centerImage: null };
    
    const formattedBenefits = [];
    
    benefits.forEach(benefit => {
      const benefitIcon = benefit.iconUrl || benefit.imageUrl || benefit.icon;
      
      if (benefitIcon) {
        formattedBenefits.push({
          icon: constructFullUrl(benefitIcon),
          title: benefit.textHeader || benefit.title || 'Benefit',
          description: benefit.textHeader || benefit.description || ''
        });
      } else if (benefit.textHeader) {
        formattedBenefits.push({
          icon: getBenefitIcon(benefit.textHeader),
          title: benefit.textHeader,
          description: benefit.textHeader
        });
      }
      
      if (benefit.descriptions && Array.isArray(benefit.descriptions)) {
        benefit.descriptions.forEach(desc => {
          if (desc.textDescription) {
            const descIcon = desc.iconUrl || desc.imageUrl || desc.icon;
            
            if (descIcon) {
              formattedBenefits.push({
                icon: constructFullUrl(descIcon),
                title: desc.textHeader || 'Benefit',
                description: desc.textDescription
              });
            } else {
              formattedBenefits.push({
                icon: getBenefitIcon(desc.textDescription),
                title: desc.textHeader || 'Benefit',
                description: desc.textDescription
              });
            }
          }
        });
      }
    });
    
    // Remove duplicates
    const uniqueBenefits = [];
    const seen = new Set();
    
    formattedBenefits.forEach(benefit => {
      const key = `${benefit.icon}-${benefit.title}`;
      if (!seen.has(key)) {
        seen.add(key);
        uniqueBenefits.push(benefit);
      }
    });
    
    const midPoint = Math.ceil(uniqueBenefits.length / 2);
    return {
      left: uniqueBenefits.slice(0, midPoint),
      right: uniqueBenefits.slice(midPoint),
      centerImage: centerImage ? constructFullUrl(centerImage) : null
    };
  }, [getBenefitIcon, constructFullUrl]);

  // Fetch product subsections
  const fetchProductSubsections = useCallback(async (productId) => {
    if (!productId || subsectionsFetchedRef.current) return;
    
    const numericId = Number(productId);
    if (isNaN(numericId)) {
      console.error("Invalid product ID for subsections:", productId);
      return;
    }
    
    try {
      setSubsectionsLoading(true);
      const response = await getProductSubsections(numericId);
      
      if (response && response.success && response.data) {
        const data = response.data;
        
        let centerImage = null;
        if (data.benefits && Array.isArray(data.benefits)) {
          const centerBenefit = data.benefits.find(b => 
            b.type === 'center' || b.position === 'center' || b.isCenterImage
          );
          
          if (centerBenefit) {
            centerImage = centerBenefit.iconUrl || centerBenefit.imageUrl || centerBenefit.icon;
          }
        }
        
        if (data.benefits && Array.isArray(data.benefits)) {
          const formattedBenefits = formatBenefitsForDisplay(data.benefits, centerImage);
          setBenefitsData(formattedBenefits);
          
          const simpleBenefits = [];
          data.benefits.forEach(benefit => {
            const benefitIcon = benefit.iconUrl || benefit.imageUrl || benefit.icon;
            
            if (benefit.textHeader) {
              simpleBenefits.push({
                icon: benefitIcon ? constructFullUrl(benefitIcon) : getBenefitIcon(benefit.textHeader),
                text: benefit.textHeader
              });
              
              if (benefit.descriptions) {
                benefit.descriptions.forEach(desc => {
                  if (desc.textDescription) {
                    const descIcon = desc.iconUrl || desc.imageUrl || desc.icon;
                    simpleBenefits.push({
                      icon: descIcon ? constructFullUrl(descIcon) : "/icons/check.svg",
                      text: desc.textDescription
                    });
                  }
                });
              }
            }
          });
          setProductBenefits(simpleBenefits);
        }
        
        if (data.beforeAfter && Array.isArray(data.beforeAfter)) {
          setBeforeAfterData(data.beforeAfter);
        }
        
        if (data.recommendations && Array.isArray(data.recommendations)) {
          const recommendations = data.recommendations
            .filter(item => item && item.recommendedProduct)
            .map(item => ({
              ...item.recommendedProduct,
              id: item.recommendedProduct.id,
              title: item.recommendedProduct.name,
              name: item.recommendedProduct.name,
              price: item.recommendedProduct.price,
              image: item.recommendedProduct.images?.[0] ? 
                     constructFullUrl(item.recommendedProduct.images[0]) : 
                     DEFAULT_IMAGE,
              media: item.recommendedProduct.media || []
            }));
          
          if (recommendations.length > 0) {
            setPairedProducts(recommendations);
          }
        }
        
        if (data.customerRecommendations && Array.isArray(data.customerRecommendations)) {
          const customerVideos = [];
          
          data.customerRecommendations.forEach((rec, index) => {
            if (rec.mediaUrl) {
              customerVideos.push({
                type: 'video',
                url: constructVideoUrl(rec.mediaUrl),
                thumbnail: null,
                index: index,
                id: `customer-video-${Date.now()}-${index}`,
                product: rec.recommendedProduct
              });
            }
          });
          
          if (customerVideos.length > 0) {
            setProductVideos(prev => {
              const existingUrls = new Set(prev.map(v => v.url));
              const newVideos = customerVideos.filter(v => !existingUrls.has(v.url));
              return [...prev, ...newVideos];
            });
          }
          
          data.recommendations?.forEach(rec => {
            if (rec.recommendedProduct?.media && Array.isArray(rec.recommendedProduct.media)) {
              const productMediaVideos = rec.recommendedProduct.media
                .filter(media => media.mediaType === 'video' && media.mediaUrl && media.isActive)
                .map((media, idx) => ({
                  type: 'video',
                  url: constructVideoUrl(media.mediaUrl),
                  thumbnail: null,
                  index: idx,
                  id: `rec-video-${Date.now()}-${idx}`,
                  product: rec.recommendedProduct
                }));
              
              if (productMediaVideos.length > 0) {
                setProductVideos(prev => {
                  const existingUrls = new Set(prev.map(v => v.url));
                  const newVideos = productMediaVideos.filter(v => !existingUrls.has(v.url));
                  return [...prev, ...newVideos];
                });
              }
            }
          });
        }
      }
      
      subsectionsFetchedRef.current = true;
    } catch (error) {
      console.error('Error fetching product subsections:', error);
    } finally {
      setSubsectionsLoading(false);
    }
  }, [formatBenefitsForDisplay, getBenefitIcon, constructVideoUrl, constructFullUrl]);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    setProduct(null);
    setProductVideos([]);
    setProductBenefits([]);
    setPairedProducts([]);
    setBeforeAfterData([]);
    setBenefitsData({ left: [], right: [], centerImage: null });
    subsectionsFetchedRef.current = false;
    setSelectedMedia(0);
    setSelectedSize("");
    setSelectedFragrance("");
    setQuantity(1);
    setExpandedSection(null);
    setVideoErrors({});
    setVideoPlayingStates({});
    
    const fetchProduct = async () => {
      try {
        setLoading(true);
        
        if (!id) {
          throw new Error("Product ID is missing from URL");
        }
        
        let isNumericId = false;
        let numericValue = null;
        
        if (!isNaN(Number(id))) {
          numericValue = Number(id);
          isNumericId = true;
        }
        else if (id.includes('-')) {
          const parts = id.split('-');
          const lastPart = parts[parts.length - 1];
          
          if (!isNaN(Number(lastPart))) {
            numericValue = Number(lastPart);
            isNumericId = true;
          }
        }
        
        if (isNumericId && numericValue) {
          const response = await getCompleteProduct(numericValue);
          const productData = response?.data || response;
          
          if (!productData) {
            throw new Error("No product data received from API");
          }
          
          processProductData(productData, numericValue);
        } 
        else {
          throw new Error(`Product with identifier "${id}" not found. Please use a numeric product ID.`);
        }
        
      } catch (err) {
        console.error('Error fetching product:', err);
        const errorMessage = err.message || err.response?.data?.message || 'Failed to load product details. Please try again.';
        setError(errorMessage);
        setProduct(null);
        setProductVideos([]);
        setProductBenefits([]);
        setPairedProducts([]);
      } finally {
        setLoading(false);
      }
    };

    const processProductData = (productData, numericId) => {
      const attributes = productData.attributes || [];
      const sizes = [];
      const fragrances = [];
      
      if (attributes.length > 0) {
        const sizeSet = new Set();
        const fragranceSet = new Set();
        
        attributes.forEach(attr => {
          if (attr.attribute1 && attr.attribute1.trim()) {
            sizeSet.add(attr.attribute1.trim());
          }
          if (attr.attribute2 && attr.attribute2.trim()) {
            fragranceSet.add(attr.attribute2.trim());
          }
        });
        
        sizes.push(...Array.from(sizeSet));
        fragrances.push(...Array.from(fragranceSet));
      }

      let productImgs = [];
      let lifestyleImgs = [];
      
      if (productData.images && Array.isArray(productData.images)) {
        productData.images.forEach((img) => {
          if (img) {
            const imageUrl = getImageSource(constructFullUrl(img));
            const imgObj = {
              type: 'image',
              url: imageUrl,
              thumbnail: imageUrl
            };
            
            const imgStr = String(img).toLowerCase();
            if (imgStr.includes("model") || imgStr.includes("lifestyle") || 
                imgStr.includes("usage") || imgStr.includes("inuse")) {
              lifestyleImgs.push(imgObj);
            } else {
              productImgs.push(imgObj);
            }
          }
        });
      }
      
      let processedImages = [...productImgs, ...lifestyleImgs];
      
      if (processedImages.length === 0) {
        processedImages = [{
          type: 'image',
          url: DEFAULT_IMAGE,
          thumbnail: DEFAULT_IMAGE
        }];
      }
      
      processedImages = processedImages.map((img, index) => ({
        ...img,
        index: index
      }));

      let processedVideos = [];
      
      if (productData.media && Array.isArray(productData.media)) {
        productData.media.forEach((media, index) => {
          if (media.mediaUrl && media.mediaType === 'video') {
            const videoUrl = constructVideoUrl(media.mediaUrl);
            
            let thumbnailUrl = null;
            if (media.thumbnailUrl && media.thumbnailUrl.trim()) {
              thumbnailUrl = constructVideoUrl(media.thumbnailUrl);
            }
            
            processedVideos.push({
              type: 'video',
              url: videoUrl,
              thumbnail: thumbnailUrl,
              index: index,
              id: `ritual-video-${index}`
            });
          }
        });
      }

      if (productData.productVideos && Array.isArray(productData.productVideos)) {
        productData.productVideos.forEach((video, index) => {
          if (video.videoUrl) {
            const videoUrl = constructVideoUrl(video.videoUrl);
            
            processedVideos.push({
              type: 'video',
              url: videoUrl,
              thumbnail: video.thumbnailUrl ? constructVideoUrl(video.thumbnailUrl) : null,
              index: processedVideos.length + index,
              id: `ritual-video-${processedVideos.length + index}`
            });
          }
        });
      }

      setProductVideos(processedVideos);

      const featuresList = Array.isArray(productData.features) 
        ? productData.features.filter(f => f && f.trim()).map(f => f.trim())
        : [];

      let description = "";
      let usageInstructions = "";
      let ingredients = "";
      let deliveryPolicy = "";
      let benefits = [];
      let frequentlyPaired = [];
      
      if (productData.productDescriptions && Array.isArray(productData.productDescriptions)) {
        productData.productDescriptions.forEach(desc => {
          const descType = desc.descriptionType?.name?.toLowerCase() || "";
          const descText = desc.descriptionText?.text || "";
          
          if (descType.includes('description')) {
            description = descText;
          } else if (descType.includes('how to use')) {
            usageInstructions = descText;
          } else if (descType.includes('ingredient')) {
            ingredients = descText;
          } else if (descType.includes('delivery') || descType.includes('return')) {
            deliveryPolicy = descText;
          }
        });
      }

      if (productData.benefits && Array.isArray(productData.benefits)) {
        benefits = productData.benefits
          .filter(benefit => benefit && typeof benefit === 'object')
          .map(benefit => {
            const benefitIcon = benefit.iconUrl || benefit.imageUrl || benefit.icon;
            return {
              icon: benefitIcon ? constructFullUrl(benefitIcon) : getBenefitIcon(benefit.textHeader || ''),
              text: benefit.textHeader || benefit.name || 'Benefit'
            };
          });
      } else if (productData.tags && Array.isArray(productData.tags)) {
        benefits = productData.tags
          .filter(tag => tag && typeof tag === 'string')
          .slice(0, 4)
          .map(tag => ({
            icon: getBenefitIcon(tag),
            text: tag
          }));
      }

      setProductBenefits(benefits);

      if (productData.relatedProducts && Array.isArray(productData.relatedProducts)) {
        frequentlyPaired = productData.relatedProducts
          .filter(p => p && p.id)
          .slice(0, 3)
          .map(p => ({
            ...p,
            image: p.image || p.images?.[0] || DEFAULT_IMAGE
          }));
      }

      setPairedProducts(frequentlyPaired);

      const extractPrice = (obj) => {
        const priceFields = [
          'price', 'sellingPrice', 'salePrice', 'offerPrice', 
          'discountedPrice', 'unitPrice', 'regularPrice', 'listPrice'
        ];
        
        for (let field of priceFields) {
          if (obj[field] !== undefined && obj[field] !== null) {
            const val = obj[field];
            if (typeof val === 'number' && !isNaN(val)) {
              return val;
            }
            if (typeof val === 'string') {
              const cleaned = val.replace(/[^0-9.-]+/g, '');
              const parsed = parseFloat(cleaned);
              if (!isNaN(parsed)) {
                return parsed;
              }
            }
          }
        }
        return 0;
      };

      const extractOriginalPrice = (obj) => {
        const originalFields = ['comparePrice', 'mrp', 'originalPrice', 'listPrice', 'regularPrice'];
        
        for (let field of originalFields) {
          if (obj[field] !== undefined && obj[field] !== null) {
            const val = obj[field];
            if (typeof val === 'number' && !isNaN(val)) {
              return val;
            }
            if (typeof val === 'string') {
              const cleaned = val.replace(/[^0-9.-]+/g, '');
              const parsed = parseFloat(cleaned);
              if (!isNaN(parsed)) {
                return parsed;
              }
            }
          }
        }
        return null;
      };

      const price = extractPrice(productData);
      const originalPrice = extractOriginalPrice(productData);

      let inStock = true;
      let stockQuantity = 999;
      
      if (productData.inStock !== undefined) {
        inStock = Boolean(productData.inStock);
      } else if (productData.isActive !== undefined) {
        inStock = Boolean(productData.isActive);
      } else if (productData.status !== undefined) {
        inStock = productData.status === 'active' || productData.status === 'in_stock';
      }
      
      if (productData.stock !== undefined && productData.stock !== null) {
        stockQuantity = Number(productData.stock);
        if (stockQuantity <= 0) {
          inStock = false;
        }
      } else if (productData.quantity !== undefined && productData.quantity !== null) {
        stockQuantity = Number(productData.quantity);
        if (stockQuantity <= 0) {
          inStock = false;
        }
      }
      
      if (productData.stock === undefined && productData.quantity === undefined && productData.inStock === undefined) {
        inStock = true;
        stockQuantity = 999;
      }

      const normalizedProduct = {
        id: productData.id || productData._id || numericId || "unknown",
        title: productData.name || productData.title || "Untitled Product",
        brand: productData.brand || productData.manufacturer || "Unknown Brand",
        price: price,
        originalPrice: originalPrice,
        description: description || productData.shortDescription || productData.description || "",
        longDescription: description || productData.description || productData.longDescription || productData.productDescription || "",
        media: processedImages,
        videos: processedVideos,
        sizes: sizes,
        fragrances: fragrances,
        tags: productData.tags || productData.categories || [],
        featuresList: featuresList,
        usageInstructions: usageInstructions || productData.howToUse || "",
        ingredients: ingredients || productData.composition || "",
        deliveryPolicy: deliveryPolicy || "",
        inStock: inStock,
        stockQuantity: stockQuantity,
        rating: productData.rating || productData.averageRating || 0,
        reviewsCount: productData.reviewsCount || productData.totalReviews || 0,
        sku: productData.sku || productData.productCode || `SKU-${productData.id || id}`,
        category: productData.category?.name || productData.categoryName || "",
        subcategory: productData.subcategory?.name || productData.subcategoryName || "",
        attributes: productData.attributes || []
      };

      if (sizes.length > 0 && !selectedSize) {
        setSelectedSize(sizes[0]);
      }
      if (fragrances.length > 0 && !selectedFragrance) {
        setSelectedFragrance(fragrances[0]);
      }

      setProduct(normalizedProduct);
      setError(null);
    };

    fetchProduct();
    
  }, [id, getImageSource, constructFullUrl, constructVideoUrl, getBenefitIcon]);

  useEffect(() => {
    if (product && id) {
      let numericId = null;
      
      if (!isNaN(Number(id))) {
        numericId = Number(id);
      } else if (id.includes('-')) {
        const parts = id.split('-');
        const lastPart = parts[parts.length - 1];
        if (!isNaN(Number(lastPart))) {
          numericId = Number(lastPart);
        }
      }
      
      if (numericId && !isNaN(numericId) && !subsectionsFetchedRef.current) {
        fetchProductSubsections(numericId);
      }
    }
  }, [product, id, fetchProductSubsections]);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const info = infoRef.current;
    if (!wrapper || !info) return;
    const onWheel = (e) => {
      const { scrollTop, scrollHeight, clientHeight } = info;
      const isScrollingDown = e.deltaY > 0;
      const atTop = scrollTop === 0;
      const atBottom = scrollTop + clientHeight >= scrollHeight - 1;
      if ((isScrollingDown && !atBottom) || (!isScrollingDown && !atTop)) {
        e.preventDefault();
        info.scrollTop += e.deltaY;
      }
    };
    wrapper.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      wrapper.removeEventListener("wheel", onWheel);
    };
  }, []);

  useEffect(() => {
    return () => {
      document.body.style.overflow = 'unset';
      document.body.classList.remove('drawer-open');
    };
  }, []);

  // handleAddToCart function with proper cart item structure
  const handleAddToCart = () => {
    if (!product) {
      alert("Product information not available!");
      return;
    }

    if (product.inStock === false) {
      alert("This product is currently out of stock!");
      return;
    }

    setIsAddingToCart(true);

    try {
      const productImage = product.media[0]?.url || DEFAULT_IMAGE;

      const selectedAttributes = [];
      if (selectedSize) {
        selectedAttributes.push(selectedSize);
      }
      if (selectedFragrance) {
        selectedAttributes.push(selectedFragrance);
      }

      const uniqueId = `${product.id}-${selectedSize || 'nosize'}-${selectedFragrance || 'nofragrance'}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      const cartItem = {
        id: product.id,
        productId: product.id,
        uniqueId: uniqueId,
        name: product.title,
        title: product.title,
        brand: product.brand,
        sku: product.sku,
        price: product.price,
        priceValue: product.price,
        basePrice: product.price,
        baseCurrency: 'USD',
        qty: quantity,
        quantity: quantity,
        image: productImage,
        img: productImage,
        img1: productImage,
        selectedSize: selectedSize || '',
        selectedFragrance: selectedFragrance || '',
        selectedAttributes: selectedAttributes,
        inStock: true,
        product: {
          id: product.id,
          name: product.title,
          price: product.price,
          images: product.media.map(m => m.url)
        }
      };

      addToCart(cartItem, quantity);

      setTimeout(() => {
        openCart();
        setIsAddingToCart(false);
      }, 100);

    } catch (error) {
      console.error("Error adding to cart:", error);
      alert("Failed to add item to cart. Please try again.");
      setIsAddingToCart(false);
    }
  };

  const handleWishlistToggle = () => {
    if (!product) return;
    const wishlistProduct = {
      id: product.id,
      title: product.title,
      price: product.price,
      image: product.media[0]?.url || DEFAULT_IMAGE,
      brand: product.brand,
      inStock: product.inStock !== false
    };
    addToWishlist(wishlistProduct);
  };

  const handleModalAddToCart = () => {
    if (!selectedProductForModal) return;
    
    try {
      const productImage = selectedProductForModal.media?.[0]?.url || 
                          selectedProductForModal.images?.[0] || 
                          DEFAULT_IMAGE;
      
      const uniqueId = `${selectedProductForModal.id}-modal-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      const cartItem = {
        id: selectedProductForModal.id,
        productId: selectedProductForModal.id,
        uniqueId: uniqueId,
        name: selectedProductForModal.title,
        title: selectedProductForModal.title,
        brand: selectedProductForModal.brand || "Unknown Brand",
        price: selectedProductForModal.price,
        priceValue: selectedProductForModal.price,
        basePrice: selectedProductForModal.price,
        baseCurrency: 'USD',
        qty: 1,
        quantity: 1,
        image: productImage,
        img: productImage,
        img1: productImage,
        inStock: true,
        product: {
          id: selectedProductForModal.id,
          name: selectedProductForModal.title,
          price: selectedProductForModal.price
        }
      };
      
      addToCart(cartItem, 1);
      
      setTimeout(() => {
        openCart();
        closeVideoModal();
      }, 100);
    } catch (error) {
      console.error("Error adding to cart from modal:", error);
      alert("Failed to add item to cart. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="product-details-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading product details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="product-details-container">
        <div className="product-not-found">
          <h2>Oops! Something went wrong</h2>
          <p>{error}</p>
          <div className="error-actions">
            <button onClick={() => navigate(-1)} className="primary-btn">Go Back</button>
            <button onClick={() => window.location.reload()} className="secondary-btn">
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-details-container">
        <div className="product-not-found">
          <h2>Product Not Found</h2>
          <p>The product you're looking for doesn't exist or has been removed.</p>
          <button onClick={() => navigate("/bestseller")} className="primary-btn">
            Browse Products
          </button>
        </div>
      </div>
    );
  }

  const displayMedia = product.media || [];
  const sizes = product.sizes || [];
  const fragrances = product.fragrances || [];
  const features = product.featuresList || [];
  const videos = productVideos || [];

  const handlePrevMedia = () => {
    setSelectedMedia((prev) => (prev === 0 ? displayMedia.length - 1 : prev - 1));
  };

  const handleNextMedia = () => {
    setSelectedMedia((prev) => (prev === displayMedia.length - 1 ? 0 : prev + 1));
  };

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const handleFragranceSelect = (fragrance) => {
    setSelectedFragrance(fragrance);
    setShowFragranceDrawer(false);
  };

  const handleOpenFragranceDrawer = () => {
    setShowFragranceDrawer(true);
  };

  const handleCloseFragranceDrawer = () => {
    setShowFragranceDrawer(false);
  };

  const renderHtmlContent = (html) => {
    if (!html) return null;
    return <div dangerouslySetInnerHTML={{ __html: html }} />;
  };

  const isAddToCartDisabled = product.inStock === false || isAddingToCart;

  return (
    <>
      <div className="product-details-container">
        <div className="breadcrumb">
          <span onClick={() => navigate("/")}>Home</span>
          <span className="separator">/</span>
          {product.category && (
            <>
              <span onClick={() => navigate(`/category/${product.category}`)}>{product.category}</span>
              <span className="separator">/</span>
            </>
          )}
          {product.subcategory && (
            <>
              <span onClick={() => navigate(`/category/${product.category}/${product.subcategory}`)}>
                {product.subcategory}
              </span>
              <span className="separator">/</span>
            </>
          )}
          <span className="current">{product.title}</span>
        </div>

        <div className="product-details-wrapper" ref={wrapperRef}>
          {/* Left Side - Images */}
          <div className="product-images-section">
            {displayMedia.length > 1 && (
              <div className="thumbnail-list">
                {displayMedia.map((media, index) => (
                  <div
                    key={index}
                    className={`thumbnail ${
                      selectedMedia === index ? "active" : ""
                    }`}
                    onClick={() => setSelectedMedia(index)}
                  >
                    <img 
                      src={media.thumbnail || media.url} 
                      alt={`${product.title} - View ${index + 1}`}
                      onError={handleImageError}
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>
            )}

            <div className="main-image-container">
              {displayMedia.length > 1 && (
                <button className="nav-btn prev-btn" onClick={handlePrevMedia}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="15 18 9 12 15 6"></polyline>
                  </svg>
                </button>
              )}
              
              <img
                src={displayMedia[selectedMedia]?.url || DEFAULT_IMAGE}
                alt={product.title}
                className="main-product-image"
                onError={handleImageError}
              />
              
              {displayMedia.length > 1 && (
                <button className="nav-btn next-btn" onClick={handleNextMedia}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </button>
              )}

              <div className="wishlist-button-container">
                <button 
                  className={`wishlist-btn ${isInWishlist(product.id) ? 'active' : ''}`}
                  onClick={handleWishlistToggle}
                  title={isInWishlist(product.id) ? "Remove from wishlist" : "Add to wishlist"}
                  aria-label={isInWishlist(product.id) ? "Remove from wishlist" : "Add to wishlist"}
                >
                  <svg 
                    width="24" 
                    height="24" 
                    viewBox="0 0 24 24" 
                    fill={isInWishlist(product.id) ? "red" : "none"} 
                    stroke="currentColor" 
                    strokeWidth="2"
                  >
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Right Side - Details */}
          <div className="product-info-section" ref={infoRef}>
            <div className="product-header">
              {product.brand && <p className="product-brand">{product.brand}</p>}
              <h1 className="product-title">{product.title}</h1>
              {product.tags && product.tags.length > 0 && (
                <div className="product-tags">
                  {product.tags.slice(0, 3).map((tag, index) => (
                    <span key={index} className="tag">{tag}</span>
                  ))}
                </div>
              )}
            </div>

            {product.rating > 0 && (
              <div className="product-rating">
                <div className="stars">
                  {"★".repeat(Math.floor(product.rating))}
                  {"☆".repeat(5 - Math.floor(product.rating))}
                </div>
                <span className="rating-value">{product.rating.toFixed(1)}</span>
                {product.reviewsCount > 0 && (
                  <span className="reviews-count">({product.reviewsCount} reviews)</span>
                )}
              </div>
            )}

            <div className="product-price-section">
              <div className="price-wrapper">
                <span className="price-value">{formatPrice(product.price)}</span>
                {product.originalPrice && product.originalPrice > product.price && (
                  <>
                    <span className="original-price">
                      {formatPrice(product.originalPrice)}
                    </span>
                    <span className="discount-badge">
                      Save {formatPrice(product.originalPrice - product.price)}
                    </span>
                  </>
                )}
              </div>
              <p className="tax-info">Tax included.</p>
            </div>

            {features.length > 0 && (
              <div className="product-features">
                <ul className="features-list">
                  {features.slice(0, 4).map((feature, index) => (
                    <li key={index} className="feature-item">
                      <span className="feature-icon">✓</span>
                      <span className="feature-text">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {sizes.length > 0 && (
              <div className="size-section">
                <label className="section-label">Size: {selectedSize}</label>
                <div className="size-options">
                  {sizes.map((size, index) => (
                    <button
                      key={index}
                      className={`size-btn ${
                        selectedSize === size ? "active" : ""
                      }`}
                      onClick={() => setSelectedSize(size)}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {fragrances.length > 0 && (
              <div className="fragrance-section">
                <label className="section-label">
                  Choose a fragrance: {selectedFragrance}
                </label>
                <div
                  className="fragrance-select-box"
                  onClick={handleOpenFragranceDrawer}
                >
                  <span>{selectedFragrance || "Select fragrance"}</span>
                  <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                    <path d="M1 1L6 6L11 1" stroke="#333" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
              </div>
            )}

            <div className="purchase-section">
              <div className="quantity-selector">
                <button
                  className="qty-btn"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={quantity <= 1}
                >
                  −
                </button>
                <span className="qty-value">{quantity}</span>
                <button
                  className="qty-btn"
                  onClick={() => setQuantity(quantity + 1)}
                  disabled={product.stockQuantity > 0 && quantity >= product.stockQuantity}
                >
                  +
                </button>
              </div>
              <button 
                className="add-to-cart-btn" 
                onClick={handleAddToCart}
                disabled={isAddToCartDisabled}
              >
                {isAddingToCart ? 'ADDING...' : 'ADD TO CART'}
              </button>
            </div>

            <div className="customer-love">
              <p>Loved by Happy Customers! 💛</p>
            </div>

            <div className="expandable-sections">
              <div className="expandable-item">
                <div
                  className="expandable-header"
                  onClick={() => toggleSection("description")}
                >
                  <h3>Description</h3>
                  <span className="expand-icon">
                    {expandedSection === "description" ? "−" : "+"}
                  </span>
                </div>
                {expandedSection === "description" && (
                  <div className="expandable-content">
                    {product.longDescription ? (
                      renderHtmlContent(product.longDescription)
                    ) : product.description ? (
                      <p>{product.description}</p>
                    ) : (
                      <p>No description available.</p>
                    )}
                  </div>
                )}
              </div>

              <div className="expandable-item">
                <div
                  className="expandable-header"
                  onClick={() => toggleSection("howto")}
                >
                  <h3>How to use</h3>
                  <span className="expand-icon">
                    {expandedSection === "howto" ? "−" : "+"}
                  </span>
                </div>
                {expandedSection === "howto" && (
                  <div className="expandable-content">
                    {product.usageInstructions ? (
                      renderHtmlContent(product.usageInstructions)
                    ) : (
                      <p>No usage instructions available.</p>
                    )}
                  </div>
                )}
              </div>

              <div className="expandable-item">
                <div
                  className="expandable-header"
                  onClick={() => toggleSection("ingredients")}
                >
                  <h3>Ingredients</h3>
                  <span className="expand-icon">
                    {expandedSection === "ingredients" ? "−" : "+"}
                  </span>
                </div>
                {expandedSection === "ingredients" && (
                  <div className="expandable-content">
                    {product.ingredients ? (
                      renderHtmlContent(product.ingredients)
                    ) : (
                      <p>No ingredients information available.</p>
                    )}
                  </div>
                )}
              </div>

              <div className="beauty-ritual-section">
                <div className="beauty-ritual-header">
                  <h3>Complete your beauty ritual</h3>
                </div>
                
                {subsectionsLoading ? (
                  <div className="ritual-loading">
                    <p>Loading ritual videos...</p>
                  </div>
                ) : videos && videos.length > 0 ? (
                  <div className="ritual-video-section">
                    <div className="ritual-videos-container">
                      <div className="ritual-videos-row">
                        {videos.map((video, index) => (
                          <div key={video.id || index} className="ritual-video-item">
                            <div 
                              className="ritual-video-wrapper"
                              onClick={(e) => {
                                if (video.product) {
                                  const modalProduct = {
                                    id: video.product.id,
                                    title: video.product.name,
                                    brand: "Unknown Brand",
                                    price: parseFloat(video.product.price) || 0,
                                    media: video.product.images ? 
                                           [{ url: constructFullUrl(video.product.images[0]) }] : 
                                           [{ url: DEFAULT_IMAGE }],
                                    images: video.product.images
                                  };
                                  openVideoModal(video, modalProduct, e);
                                } else {
                                  openVideoModal(video, product, e);
                                }
                              }}
                            >
                              <video
                                id={video.id}
                                className="ritual-video-player"
                                poster={video.thumbnail || undefined}
                                preload="metadata"
                                playsInline
                                loop
                                muted
                                onError={(e) => handleVideoError(index, e)}
                                onLoadedData={(e) => {
                                  e.target.style.opacity = '1';
                                }}
                                onPlay={() => handleVideoPlay(video.id)}
                                onPause={() => handleVideoPause(video.id)}
                              >
                                <source 
                                  src={video.url} 
                                  type="video/mp4" 
                                />
                                Your browser does not support the video tag.
                              </video>
                              {!videoErrors[index] && (
                                <button 
                                  className="custom-play-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleVideoPlay(video.id, e);
                                  }}
                                  aria-label={videoPlayingStates[video.id] ? "Pause video" : "Play video"}
                                >
                                  {videoPlayingStates[video.id] ? (
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" width="20" height="20">
                                      <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                                    </svg>
                                  ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" width="20" height="20">
                                      <path d="M8 5v14l11-7z"/>
                                    </svg>
                                  )}
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="ritual-video-section">
                    <p style={{ color: '#999', fontSize: '14px', textAlign: 'left', padding: '10px 0' }}>
                      No ritual videos available
                    </p>
                  </div>
                )}
              </div>

              <div className="expandable-item">
                <div
                  className="expandable-header"
                  onClick={() => toggleSection("delivery")}
                >
                  <h3>Delivery and return policy</h3>
                  <span className="expand-icon">
                    {expandedSection === "delivery" ? "−" : "+"}
                  </span>
                </div>
                {expandedSection === "delivery" && (
                  <div className="expandable-content">
                    {product.deliveryPolicy ? (
                      renderHtmlContent(product.deliveryPolicy)
                    ) : (
                      <p>No delivery policy information available.</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Fragrance Drawer */}
        {showFragranceDrawer && fragrances.length > 0 && (
          <>
            <div
              className="drawer-overlay"
              onClick={handleCloseFragranceDrawer}
            />
            <div className="fragrance-drawer">
              <div className="drawer-header">
                <h3>Choose a fragrance</h3>
                <button
                  className="close-btn"
                  onClick={handleCloseFragranceDrawer}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
              <div className="drawer-content">
                {fragrances.map((fragrance, index) => (
                  <div
                    key={index}
                    className={`fragrance-option ${
                      selectedFragrance === fragrance ? "selected" : ""
                    }`}
                    onClick={() => handleFragranceSelect(fragrance)}
                  >
                    <span>{fragrance}</span>
                    {selectedFragrance === fragrance && (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* VIDEO MODAL */}
        {isVideoModalOpen && selectedVideo && selectedProductForModal && (
          <div className="video-modal-overlay" onClick={closeVideoModal}>
            <div className="video-modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="video-modal-close" onClick={closeVideoModal}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
              
              <div className="video-modal-player-wrapper">
                <video
                  id="modal-video"
                  className="video-modal-player"
                  src={selectedVideo.url}
                  poster={selectedVideo.thumbnail || undefined}
                  controls
                  autoPlay
                  playsInline
                />
              </div>

              <div className="video-modal-product-card">
                <div className="video-modal-product-info">
                  <img 
                    src={selectedProductForModal.media?.[0]?.url || 
                         selectedProductForModal.images?.[0] || 
                         DEFAULT_IMAGE} 
                    alt={selectedProductForModal.title}
                    className="video-modal-product-image"
                    onError={handleImageError}
                  />
                  <div className="video-modal-product-details">
                    <span className="video-modal-product-brand">{selectedProductForModal.brand || "Unknown Brand"}</span>
                    <h4 className="video-modal-product-title">{selectedProductForModal.title}</h4>
                    <span className="video-modal-product-price">{formatPrice(selectedProductForModal.price)}</span>
                  </div>
                </div>
                <button className="video-modal-add-to-cart" onClick={handleModalAddToCart}>
                  ADD TO CART
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
       <AboutVideo />
      {/* Display subsections data using existing components */}
      {(benefitsData.left.length > 0 || benefitsData.right.length > 0 || benefitsData.centerImage) && (
        <ProductBenefits benefits={benefitsData} />
      )}
      <ProductShowcase/>
      <HomeTrendingSection />
      <TestimonialSlider />
      <FaqSection />
      <AboutSection />
      <HomeFeatures />
      {beforeAfterData.length > 0 && <BeforeAfterSlider data={beforeAfterData} />}
      <HomeBestsellersSection />
    </>
  );
};

export default ProductDetailsPage;